import { Button, Icon, IconName } from '@fashionnovateam/nova-ui-toolkit';

import { ReactNode } from 'react';
import { useParamFilter } from '../../Lib/Hooks/Filters/useParamFilter';
import { possibleParams } from '../../Lib/Hooks/ItemService';
import { VPStages } from './enums/stage.enums';

type EmptyListProps = {
  onAddItemsClick: () => void;
};

type EmptyListButton = {
  onClick: () => void;
  text: string;
  iconName: IconName;
};

type EmptyListGenericProps = {
  iconName: IconName;
  title: string;
  description: string | ReactNode;
  quote?: string;
  button?: EmptyListButton;
};

/**
 * Renders a generic empty list representation component.
 */
function EmptyListGeneric({
  iconName,
  title,
  description,
  quote,
  button,
}: EmptyListGenericProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full w-full text-center">
      <div className="text-primary-700">
        <Icon name={iconName} variant="solid" size="32px" />
      </div>
      <div className="mt-5 text-3xl font-medium">{title}</div>
      <div className="flex flex-col gap-2 text-center mt-2">
        <div className="text-md font-normal">{description}</div>
        {quote && (
          <div className="text-xs text-base-disabled font-normal">{quote}</div>
        )}
      </div>
      {button && (
        <Button
          data-testid="empty-list-button"
          onClick={button.onClick}
          className="mt-6"
        >
          <Icon name={button.iconName} size="16px" variant="solid" />
          {button.text}
        </Button>
      )}
    </div>
  );
}

/**
 * This component is used to display the empty list message for the sell to us page
 */
function EmptyList({ onAddItemsClick }: EmptyListProps) {
  const { paramFilters } = useParamFilter(possibleParams);

  const hasActiveFilters =
    (paramFilters.search && paramFilters.search.trim() !== '') ||
    paramFilters.category?.some((category) => category.trim() !== '') ||
    paramFilters.class?.some((className) => className.trim() !== '');

  if (hasActiveFilters) {
    return (
      <EmptyListGeneric
        iconName="star"
        title="No search results"
        description="Sorry, we couldn't find any items that match your search. Make sure you're searching in the correct status and try again."
      />
    );
  }

  switch (paramFilters.stage) {
    case VPStages.PENDING_FN:
      return (
        <EmptyListGeneric
          iconName="star"
          title="Sell us your products"
          description={
            <>
              Items you&apos;re selling to us and waiting on our review will
              show up here. <br />
            </>
          }
          button={{
            onClick: onAddItemsClick,
            text: 'Sell now',
            iconName: 'money-dollar-bag',
          }}
        />
      );
    case VPStages.NEEDS_DETAILS:
      return (
        <EmptyListGeneric
          iconName="bag/paper-bag"
          title="No items need additional details now."
          description={
            <>
              We&apos;ll list your items here whenever we show interest in what
              you&apos;ve sent. At that point, <br />
              we&apos;ll ask you for a few extra details do we can keep things
              moving.
            </>
          }
        />
      );
    case VPStages.DETAILS_SENT:
      return (
        <EmptyListGeneric
          iconName="bag/paper-bag"
          title="No items right now."
          description={
            <>
              Once you&apos;ve provided the additional details for any items
              (and we haven&apos;t passed or
              <br />
              created a purchase order), you&apos;ll see them here.
            </>
          }
        />
      );
    case VPStages.PO_CREATED:
      return (
        <EmptyListGeneric
          iconName="bag/paper-bag"
          title="No items here right now."
          description={
            <>
              Whenever we issue a Purchase Order for any items your propose,
              they&apos;ll appear here.
            </>
          }
        />
      );
    case VPStages.PASSED:
      return (
        <EmptyListGeneric
          iconName="bag/paper-bag"
          title="No items here right now."
          description={
            <>
              If we decide to pass on any of your proposed items, they&apos;ll
              appear here.
            </>
          }
        />
      );
    default:
      return (
        <EmptyListGeneric
          iconName="bag/paper-bag"
          title="No items yet."
          description={
            <>
              As soon as you start uploading items you&apos;d like to sell us,
              you can see them all here--
              <br />
              regardless of status.
            </>
          }
          button={{
            onClick: onAddItemsClick,
            text: 'Sell now',
            iconName: 'money-dollar-bag',
          }}
        />
      );
  }
}

export default EmptyList;
export { EmptyListGeneric };
